import {Field, Input, InputProps} from '@chakra-ui/react'
import {forwardRef} from 'react'
import type {Country} from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {useDefaultCountry} from '../../hooks/default-country.js'
import {useFieldContext} from '../../integrations/form/index.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'

type PhoneValue = string | undefined // E.164 (e.g. +407xxxxxxxx) or undefined

const ChakraPhoneNumberInput = forwardRef<HTMLInputElement, InputProps>(function ChakraPhoneNumberInput(props, ref) {
  return <Input ref={ref} backgroundColor="bg" {...props} />
})

interface PhoneFieldProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  label: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  helpText?: string
  defaultCountry?: Country
  international?: boolean
  countrySelectDisabled?: boolean
}

const style = {width: '100%'}

export function PhoneField({
  required,
  disabled,
  placeholder,
  helpText,
  defaultCountry,
  international = true,
  countrySelectDisabled,
  ...inputProps
}: PhoneFieldProps) {
  const field = useFieldContext<PhoneValue>()
  const contextCountry = useDefaultCountry()
  // fall back to the organisation's country (the licence country) before the generic 'DE' default
  const resolvedDefaultCountry = defaultCountry ?? contextCountry ?? 'DE'

  return (
    <FieldRoot required={required} disabled={disabled}>
      <Field.Label>
        {inputProps.label}
        {required && <FieldRequiredIndicator />}
      </Field.Label>

      <PhoneInput
        name={field.name}
        value={field.state.value}
        onChange={value => field.handleChange(value)}
        onBlur={() => field.handleBlur()}
        disabled={disabled}
        required={required}
        defaultCountry={resolvedDefaultCountry}
        international={international}
        countrySelectProps={{disabled: countrySelectDisabled}}
        inputComponent={ChakraPhoneNumberInput}
        style={style}
        inputProps={{
          placeholder,
          ...inputProps,
        }}
      />

      {helpText && <Field.HelperText>{helpText}</Field.HelperText>}
      <ErrorMessage />
    </FieldRoot>
  )
}
